import { ReactComponent as PlayButtonSVG } from '@/assets/images/svg/buttons/play-button.svg';
import YouTubeVideo from '@/components/YouTubeVideo';
import gtm from '@/lib/gtm';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { extractYouTubeId } from '@/lib/youtube';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { remCalc } from '@/styles/typography/utils';
import { FC, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

export interface InlineVideoProps {
  videoSrc?: string;
  videoPosterSrc?: string;
  youtubeVideoUrl?: string;
  hasSound: boolean;
  onInitialPlay?: () => void;
}

const InlineVideo: FC<InlineVideoProps> = (props) => {
  const [videoPlayed, setVideoPlayed] = useState(false);

  const { ref, inView } = useInView({
    threshold: 0.9,
    trackVisibility: true,
    delay: 100,
    triggerOnce: true,
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlayButton, setShowPlayButton] = useState(props.hasSound);
  const videoType = props.videoSrc ? 'asset' : props.youtubeVideoUrl ? 'youtube' : false;

  useEffect(() => {
    if (inView && videoRef.current) {
      const video = videoRef.current;
      const isVideoPlaying = !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2);

      if (!isVideoPlaying) {
        video.play().catch((e) => {
          // Commonly a NotAllowedError, which we can just silently ignore
        });
      }
    }
  }, [inView, videoRef.current]);

  switch (videoType) {
    case 'asset':
      if (!props.videoSrc) return null;

      return (
        <VideoWrapper
          ref={ref}
          style={{
            cursor: props.hasSound ? 'pointer' : 'auto',
          }}
          onClick={(e) => {
            if (props.hasSound && showPlayButton) {
              setShowPlayButton(false);

              const video = videoRef.current!;

              video.pause();
              video.currentTime = 0;
              video.muted = false;
              video.play().catch((e) => {
                // Commonly a NotAllowedError, which we can just silently ignore
              });
            }
          }}
        >
          {props.hasSound && showPlayButton && (
            <PlayButtonWrapper>
              <PlayButton />
            </PlayButtonWrapper>
          )}

          <video
            poster={props.videoPosterSrc}
            autoPlay={false}
            loop={!props.hasSound || showPlayButton}
            muted={!props.hasSound || showPlayButton}
            src={props.videoSrc}
            ref={videoRef}
            controls={props.hasSound && !showPlayButton}
            playsInline
            onClick={(e) => {
              if (!props.hasSound || showPlayButton) {
                e.preventDefault();
              }
            }}
            onPlay={() => {
              if (!videoPlayed) {
                setVideoPlayed(true);
                if (props.onInitialPlay) props.onInitialPlay();
              }
            }}
          />
        </VideoWrapper>
      );
    case 'youtube':
      const youtubeVideoUrl = props.youtubeVideoUrl;

      if (!youtubeVideoUrl) return null;

      const youtubeVideoId = extractYouTubeId(youtubeVideoUrl);

      if (!youtubeVideoId) return null;

      return (
        <VideoWrapper ref={ref}>
          <YouTubeVideo
            videoId={youtubeVideoId}
            coverSrc={props.videoPosterSrc}
            onInitialPlay={() => {
              gtm.track('play_video', {
                video_url: maybeNotSet(youtubeVideoUrl),
                video_youtube_id: maybeNotSet(youtubeVideoId),
              });
            }}
          />
        </VideoWrapper>
      );
    default:
      return null;
  }
};

export default InlineVideo;

InlineVideo.defaultProps = {
  hasSound: false,
};

// Styled Components
const PlayButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${hexToRGBA(COLORS.BLACK, 0.15)};
`;

const PlayButton = styled(PlayButtonSVG)`
  position: absolute;
  top: calc(50% - ${remCalc(140 / 2)});
  left: calc(50% - ${remCalc(140 / 2)});
  width: ${remCalc(140)};
  z-index: 2;
  transform: scale(1);
  transform-origin: center;
  transition: transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out;
  box-shadow: 0 0 30px 10px ${hexToRGBA(COLORS.BLACK, 0.25)};
  border-radius: 100%;
  pointer-events: none;
  background-color: ${COLORS.WHITE};
`;

const VideoWrapper = styled.div`
  position: relative;
  &:hover {
    ${PlayButton} {
      transform: scale(1.1);
    }
  }

  > video {
    border-radius: ${remCalc(3)};
  }
`;
